import { useState } from "react";
import { getEligibility, isNotFound } from "../../api/client";
import { Button } from "../../components/Button";
import { Callout } from "../../components/Callout";
import { Field } from "../../components/Field";
import { Rows } from "../../components/Rows";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useConfig } from "../../hooks/useConfig";
import { formatBalance, formatCount, formatShare, formatTokenAmount } from "../../lib/format";
import { isSolanaAddress } from "../../lib/validate";
import { connectPhantom, hasPhantom } from "../../wallets/phantom";
import { WalletMissingError, walletErrorMessage } from "../../wallets/errors";
import { StepShell } from "./StepShell";
import type { AirdropFlow } from "./useAirdropFlow";

const MODE_LABEL = {
  pro: "pro rata by snapshot balance",
  cap5: "pro rata, capped at 5% per wallet",
} as const;

export function StepEligibility({ flow }: { flow: AirdropFlow }) {
  const { config } = useConfig();
  const [notEligible, setNotEligible] = useState<string | null>(null);

  const check = useAsyncAction(async (address: string) => {
    setNotEligible(null);
    try {
      const result = await getEligibility(address.trim());
      flow.setEligibility(result);
      return result;
    } catch (error) {
      if (isNotFound(error)) {
        flow.setEligibility(null);
        setNotEligible(address.trim());
        return null;
      }
      throw error;
    }
  }, (error) => (error instanceof Error ? error.message : "Could not check this address."));

  const connect = useAsyncAction(async () => {
    if (!hasPhantom()) throw new WalletMissingError("phantom");
    const address = await connectPhantom();
    flow.setSolanaAddress(address);
    await check.run(address);
    return address;
  }, (error) => {
    if (error instanceof WalletMissingError) {
      flow.setMissingWallet("phantom");
      return "";
    }
    return walletErrorMessage(error, "Could not connect Phantom.");
  });

  const typed = flow.solanaAddress.trim();
  const valid = typed.length > 0 && isSolanaAddress(typed);
  const eligibility = flow.eligibility;
  const decimals = config?.token.decimals ?? 18;
  const mode = config?.pool.mode ?? "pro";
  const error = check.error || connect.error || null;

  return (
    <StepShell number={1} title="Check eligibility" active>
      <p className="body">
        The airdrop goes to the top 200 $INDEXSY holders in the Solana snapshot. Paste that wallet
        address, or connect Phantom and we will read it.
      </p>

      <Field
        label="Solana address"
        placeholder="Your $INDEXSY wallet"
        value={flow.solanaAddress}
        onChange={(event) => {
          flow.setSolanaAddress(event.target.value);
          setNotEligible(null);
        }}
        hint={
          config
            ? `${formatCount(config.snapshot.eligibleWallets)} wallets are eligible. The cutoff was ${formatBalance(config.snapshot.cutoffBalance)} $INDEXSY.`
            : undefined
        }
        error={typed.length > 0 && !valid ? "That does not look like a Solana address." : null}
      />

      <div className="form-actions">
        <Button
          onClick={() => void check.run(typed)}
          disabled={!valid}
          pending={check.pending}
        >
          Check eligibility
        </Button>
        <Button variant="ghost" onClick={() => void connect.run()} pending={connect.pending}>
          Connect Phantom
        </Button>
      </div>

      {error ? (
        <Callout tone="error" role="alert">
          {error}
        </Callout>
      ) : null}

      {notEligible ? (
        <Callout tone="neutral" role="status">
          <strong>Not in the top 200.</strong> That wallet is not on the snapshot list, so it has no
          allocation. The list is fixed at the snapshot and cannot be changed.
        </Callout>
      ) : null}

      {eligibility ? (
        <div className="card">
          <Rows
            items={[
              { key: "rank", label: "Rank", value: `#${eligibility.rank} of 200` },
              {
                key: "balance",
                label: "$INDEXSY at snapshot",
                value: formatBalance(eligibility.indexsyBalance),
              },
              {
                key: "share",
                label: `Share of the pool (${MODE_LABEL[mode]})`,
                value: formatShare(eligibility.share),
              },
              {
                key: "allocation",
                label: "Allocation",
                value: eligibility.allocation
                  ? `${formatTokenAmount(eligibility.allocation, decimals)} ${config?.token.symbol ?? "TISM"}`
                  : "Set once the pool is funded",
              },
            ]}
          />
        </div>
      ) : null}
    </StepShell>
  );
}
